import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseStockAndWaxesDataService } from '../../../base-stock-and-waxes-data.service';
import { BwFeedbackRequest, TableData } from '../../interfaces';

export enum FeedbackAction {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED'
}

export interface FeedbackData {
  qualityConfirmation: string;
  action: FeedbackAction | '';
  type: string;
  comments: string;
}

export interface FeedbackDialogData {
  selectedMarkers?: TableData[];
  existingFeedback?: any;
}

@Component({
  selector: 'app-feedback-dialog',
  standalone: false,
  templateUrl: './feedback-dialog.component.html',
  styleUrl: './feedback-dialog.component.scss'
})
export class FeedbackDialogComponent {
  feedbackData: FeedbackData = {
    qualityConfirmation: '',
    action: '',
    type: '',
    comments: ''
  };

  selectedItems: string[] = [];
  selectedMarkers: TableData[] = [];
  FeedbackAction = FeedbackAction; 
  isSubmitting = false;

  constructor(
    private baseStockAndWaxesDataService: BaseStockAndWaxesDataService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<FeedbackDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FeedbackDialogData
  ) {
    if (data && data.selectedMarkers) {
      this.selectedMarkers = data.selectedMarkers;
      this.selectedItems = data.selectedMarkers.map(marker => 
        marker.markerName || marker.actualMarker || ''
      );
    }
    
    if (data && data.existingFeedback) {
      let qualityConfirmationValue = '';
      if (data.existingFeedback.qualityConfirmation !== null && data.existingFeedback.qualityConfirmation !== undefined) {
        qualityConfirmationValue = data.existingFeedback.qualityConfirmation ? 'Yes' : 'No';
      }
      
      this.feedbackData = {
        qualityConfirmation: qualityConfirmationValue,
        action: data.existingFeedback.action as FeedbackAction,
        type: data.existingFeedback.reason || '',
        comments: data.existingFeedback.comments || ''
      };
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.isFormValid()) {
      const feedbackRequests: BwFeedbackRequest[] = this.selectedMarkers.map(marker => ({
        marker: marker.markerName || marker.actualMarker || '',
        qualityConfirmation: this.feedbackData.qualityConfirmation === 'Yes',
        feedbackType: this.feedbackData.action as string, 
        feedbackReason: this.feedbackData.type || '',
        comments: this.feedbackData.comments,
        lastUpdated: marker.lastUpdated || new Date(),
        actualMarker: marker.actualMarker || '',
        actualMarkerName: marker.markerName || ''
      }));

      this.isSubmitting = true; 
      this.baseStockAndWaxesDataService.addBaseAndWavesFeedback(feedbackRequests).subscribe({
        next: (response) => {
          this.snackBar.open('Feedback submitted successfully!', 'Close', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
          });
          this.dialogRef.close({
            feedbackData: this.feedbackData,
            selectedMarkers: this.selectedMarkers
          });
        },
        error: (error) => {
          console.error('Error submitting feedback:', error);
          this.snackBar.open('Error submitting feedback. Please try again.', 'Close', {
            duration: 5000,
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        },
        complete: () => {
          this.isSubmitting = false; 
        }
      });
    }
  }

  isFormValid(): boolean {
    const hasQualityConfirmation = !!this.feedbackData.qualityConfirmation;
    const hasAction = !!this.feedbackData.action;
    const hasComments = !!this.feedbackData.comments.trim();
    const hasTypeWhenRejected = this.feedbackData.action !== FeedbackAction.REJECTED || !!this.feedbackData.type;
    
    return hasQualityConfirmation && hasAction && hasComments && hasTypeWhenRejected;
  }

  onActionChange(): void {
    if (this.feedbackData.action === FeedbackAction.ACCEPTED) {
      this.feedbackData.type = '';
    }
  }
}
